import { useState } from "react";
import { useTranslation } from "react-i18next";
import { Bot, Plus, Sparkles, Trash2 } from "lucide-react";
import {
  PUBLISHER,
  type AggBot,
  type AiProvider,
  PROVIDER_PRESETS,
  useAddProvider,
  useAiProviders,
  useBots,
  useCreateBot,
  useDisconnectBot,
  useCheckTranslator,
  useRemoveProvider,
  useRemoveTranslator,
  useSaveTranslator,
  useTestProvider,
  useTranslator,
  useUpdateProvider,
} from "@/lib/aggregator";
import { formatAccountNo } from "@/lib/accounts";
import { formatNumber, formatRelative } from "@/lib/format";
import { pushToast } from "@/components/Toaster";
import { Alert } from "@/components/ui/alert";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  ConfirmDialog,
  Dialog,
  DialogBody,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { SimpleSelect } from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { AccountSelect } from "./AccountSelect";
import { ICON_BUTTON, errorText } from "./helpers";

/**
 * What the aggregator is connected to: the bots that publish and the
 * language-model services that rewrite. Both are the company's secrets —
 * making and removing them is the admin's; everybody sees what there is.
 * A bot is made by one of the company's accounts at @BotFather: nobody goes
 * into Telegram and nobody handles a token.
 * Asking a service whether it answers spends a request of the company's, and
 * so is the operator's (`canEdit`).
 */
export function ConnectionsDialog({
  open,
  onOpenChange,
  canManage,
  canEdit,
  tab,
  onTabChange,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  canManage: boolean;
  canEdit: boolean;
  tab: "bots" | "ai";
  onTabChange: (tab: "bots" | "ai") => void;
}) {
  const { t } = useTranslation();
  const byAccount = PUBLISHER === "account";
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent size="lg">
        <DialogHeader>
          <DialogTitle>
            {t(byAccount ? "aggregator.connections.title_account" : "aggregator.connections.title")}
          </DialogTitle>
          <DialogDescription>
            {t(
              byAccount
                ? "aggregator.connections.subtitle_account"
                : "aggregator.connections.subtitle",
            )}
          </DialogDescription>
        </DialogHeader>
        <DialogBody>
          {!canManage && (
            <Alert tone="info" className="mb-4">
              {t("aggregator.connections.admin_only")}
            </Alert>
          )}
          {byAccount ? (
            // The account that runs a channel publishes: no bot to connect.
            <>
              <Providers canManage={canManage} canEdit={canEdit} />
              <TranslatorBlock canManage={canManage} canEdit={canEdit} />
            </>
          ) : (
          <Tabs value={tab} onValueChange={(value) => onTabChange(value as "bots" | "ai")}>
            <TabsList>
              <TabsTrigger value="bots">
                <Bot size={15} strokeWidth={2} aria-hidden="true" />
                {t("aggregator.connections.bots")}
              </TabsTrigger>
              <TabsTrigger value="ai">
                <Sparkles size={15} strokeWidth={2} aria-hidden="true" />
                {t("aggregator.connections.ai")}
              </TabsTrigger>
            </TabsList>
            <TabsContent value="bots">
              <Bots canManage={canManage} />
            </TabsContent>
            <TabsContent value="ai">
              <Providers canManage={canManage} canEdit={canEdit} />
              <TranslatorBlock canManage={canManage} canEdit={canEdit} />
            </TabsContent>
          </Tabs>
          )}
        </DialogBody>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            {t("common.close")}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

/* -------------------------------------------------------------- translator */

/** DeepL on the company's key: translation without a language model. */
function TranslatorBlock({ canManage, canEdit }: { canManage: boolean; canEdit: boolean }) {
  const { t } = useTranslation();
  const translator = useTranslator();
  const save = useSaveTranslator();
  const check = useCheckTranslator();
  const remove = useRemoveTranslator();
  const [key, setKey] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [removing, setRemoving] = useState(false);
  const shown = translator.data;
  const usage =
    shown && shown.used !== null && shown.limit
      ? t("aggregator.translator.usage", {
          used: formatNumber(shown.used),
          limit: formatNumber(shown.limit),
        })
      : null;

  return (
    <div className="mt-6 space-y-3 border-t border-border pt-5">
      <div>
        <h3 className="text-sm font-semibold text-fg-primary">{t("aggregator.translator.title")}</h3>
        <p className="mt-0.5 text-xs leading-relaxed text-fg-muted">
          {t("aggregator.translator.hint")}{" "}
          <a
            href="https://www.deepl.com/pro-api"
            target="_blank"
            rel="noreferrer noopener"
            className="text-accent hover:underline"
          >
            deepl.com/pro-api
          </a>
        </p>
      </div>
      {shown ? (
        <div className="space-y-2 rounded-xl border border-border bg-bg-primary px-4 py-3">
          <div className="flex flex-wrap items-center gap-2 text-sm">
            <span className="font-semibold text-fg-primary">DeepL</span>
            <Badge tone={shown.last_error ? "danger" : "success"}>
              {t(shown.last_error ? "aggregator.translator.broken" : "aggregator.translator.works")}
            </Badge>
            <span className="font-mono text-xs text-fg-muted">{shown.key_hint}</span>
            {shown.free && <span className="text-xs text-fg-muted">{t("aggregator.translator.free")}</span>}
          </div>
          {usage && <p className="text-xs text-fg-secondary">{usage}</p>}
          {shown.last_error && <p className="text-xs text-danger">{shown.last_error}</p>}
          <div className="flex flex-wrap gap-2">
            {canEdit && (
              <Button
                size="sm"
                variant="outline"
                loading={check.isPending}
                onClick={async () => {
                  try {
                    await check.mutateAsync(undefined);
                  } catch (err) {
                    pushToast({ kind: "error", text: errorText(err, t("aggregator.action_error")) });
                  }
                }}
              >
                {t("aggregator.translator.check")}
              </Button>
            )}
            {canManage && (
              <Button size="sm" variant="ghost" onClick={() => setRemoving(true)}>
                {t("aggregator.translator.remove")}
              </Button>
            )}
          </div>
        </div>
      ) : (
        <p className="text-sm text-fg-muted">{t("aggregator.translator.none")}</p>
      )}
      {canManage && (
        <div className="flex flex-wrap items-end gap-2">
          <div className="min-w-[14rem] flex-1 space-y-1.5">
            <Label htmlFor="agg_deepl_key">
              {t(shown ? "aggregator.translator.replace_key" : "aggregator.translator.key")}
            </Label>
            <Input
              id="agg_deepl_key"
              type="password"
              autoComplete="off"
              value={key}
              onChange={(e) => {
                setKey(e.target.value);
                setError(null);
              }}
              placeholder="xxxxxxxx-xxxx-xxxx-xxxx-xxxxxxxxxxxx:fx"
            />
          </div>
          <Button
            loading={save.isPending}
            disabled={key.trim().length < 10 || save.isPending}
            onClick={async () => {
              setError(null);
              try {
                await save.mutateAsync(key.trim());
                setKey("");
                pushToast({ kind: "success", text: t("aggregator.translator.saved") });
              } catch (err) {
                setError(errorText(err, t("aggregator.action_error")));
              }
            }}
          >
            {t("aggregator.translator.save")}
          </Button>
        </div>
      )}
      {error && <Alert tone="danger">{error}</Alert>}
      <ConfirmDialog
        open={removing}
        onOpenChange={setRemoving}
        tone="danger"
        title={t("aggregator.translator.remove_title")}
        description={t("aggregator.translator.remove_hint")}
        confirmText={t("aggregator.translator.remove")}
        cancelText={t("common.cancel")}
        onConfirm={async () => {
          try {
            await remove.mutateAsync(undefined);
          } catch (err) {
            pushToast({ kind: "error", text: errorText(err, t("aggregator.action_error")) });
          }
        }}
      />
    </div>
  );
}

/* -------------------------------------------------------------------- bots */

function Bots({ canManage }: { canManage: boolean }) {
  const { t } = useTranslation();
  const bots = useBots();
  const create = useCreateBot();
  const disconnect = useDisconnectBot();
  const [account, setAccount] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [removing, setRemoving] = useState<AggBot | null>(null);

  const submit = async () => {
    setError(null);
    try {
      const bot = await create.mutateAsync(account);
      pushToast({ kind: "success", text: t("aggregator.bots.connected", { name: bot.username }) });
    } catch (err) {
      // The account stays chosen: asked again, it reads @BotFather's answer
      // to the end instead of beginning another bot.
      setError(errorText(err, t("aggregator.bots.no_answer")));
    }
  };

  return (
    <div className="space-y-5">
      {bots.data && bots.data.length === 0 && (
        <p className="text-sm text-fg-muted">{t("aggregator.bots.empty")}</p>
      )}
      <ul className="space-y-2">
        {bots.data?.map((bot) => (
          <li
            key={bot.id}
            className="flex flex-wrap items-center gap-3 rounded-xl border border-border bg-bg-primary px-4 py-3"
          >
            <div className="min-w-0 flex-1">
              <div className="truncate font-semibold text-fg-primary">@{bot.username}</div>
              <div className="truncate text-xs text-fg-muted">
                {bot.name} · {t("aggregator.bots.channels", { count: bot.channels })}
                {bot.account_seq_no !== null &&
                  ` · ${t("aggregator.bots.made_by", { no: formatAccountNo(bot.account_seq_no) })}`}
              </div>
            </div>
            {bot.problem ? (
              <Badge tone="danger">{t(`aggregator.problem.${bot.problem}`, bot.problem)}</Badge>
            ) : (
              <Badge tone="success">{t("aggregator.bots.ok")}</Badge>
            )}
            {canManage && (
              <button
                type="button"
                className={ICON_BUTTON}
                aria-label={t("aggregator.bots.remove")}
                onClick={() => setRemoving(bot)}
              >
                <Trash2 size={16} strokeWidth={2} aria-hidden="true" />
              </button>
            )}
          </li>
        ))}
      </ul>

      {canManage && (
        <div className="space-y-3 rounded-xl border border-border px-4 py-4">
          <div>
            <h3 className="text-sm font-semibold text-fg-primary">{t("aggregator.bots.add_title")}</h3>
            <p className="mt-1 text-xs leading-relaxed text-fg-muted">
              {t("aggregator.bots.make_hint")}
            </p>
          </div>
          <div className="space-y-2">
            <Label>{t("aggregator.bots.account")}</Label>
            <AccountSelect
              value={account}
              onChange={(id) => {
                setAccount(id);
                setError(null);
              }}
              disabled={create.isPending}
            />
            <p className="text-xs leading-relaxed text-fg-muted">{t("aggregator.bots.risk")}</p>
          </div>
          {error && <Alert tone="danger">{error}</Alert>}
          <div className="flex flex-wrap items-center gap-3">
            <Button
              onClick={submit}
              loading={create.isPending}
              disabled={!account || create.isPending}
              iconLeft={<Plus size={16} strokeWidth={2} aria-hidden="true" />}
            >
              {t("aggregator.bots.connect")}
            </Button>
            {create.isPending && (
              <span className="text-xs text-fg-muted">{t("aggregator.bots.making")}</span>
            )}
          </div>
        </div>
      )}

      <ConfirmDialog
        open={removing !== null}
        onOpenChange={(o) => !o && setRemoving(null)}
        tone="danger"
        title={t("aggregator.bots.remove_confirm", { name: removing?.username ?? "" })}
        description={t("aggregator.bots.remove_hint")}
        confirmText={t("aggregator.bots.remove")}
        cancelText={t("common.cancel")}
        onConfirm={async () => {
          if (!removing) return;
          try {
            await disconnect.mutateAsync(removing.id);
            pushToast({ kind: "success", text: t("aggregator.bots.removed") });
          } catch (err) {
            pushToast({ kind: "error", text: errorText(err, t("aggregator.action_error")) });
          }
        }}
      />
    </div>
  );
}

/* --------------------------------------------------------------- providers */

function Providers({ canManage, canEdit }: { canManage: boolean; canEdit: boolean }) {
  const { t } = useTranslation();
  const providers = useAiProviders();
  const add = useAddProvider();
  const update = useUpdateProvider();
  const remove = useRemoveProvider();
  const test = useTestProvider();
  const [preset, setPreset] = useState<string>(PROVIDER_PRESETS[0].id);
  const [form, setForm] = useState({
    name: PROVIDER_PRESETS[0].name as string,
    base_url: PROVIDER_PRESETS[0].base_url as string,
    model: PROVIDER_PRESETS[0].model as string,
    api_key: "",
  });
  const [error, setError] = useState<string | null>(null);
  const [removing, setRemoving] = useState<AiProvider | null>(null);

  const pick = (id: string) => {
    setPreset(id);
    const found = PROVIDER_PRESETS.find((p) => p.id === id);
    if (found) {
      setForm((f) => ({ ...f, name: found.name, base_url: found.base_url, model: found.model }));
    }
  };

  const submit = async () => {
    setError(null);
    try {
      await add.mutateAsync({ ...form, api_key: form.api_key.trim(), requests_per_minute: 20 });
      setForm((f) => ({ ...f, api_key: "" }));
      pushToast({ kind: "success", text: t("aggregator.ai.added") });
    } catch (err) {
      setError(errorText(err, t("aggregator.action_error")));
    }
  };

  // The service asked first is the first one switched on, not the first listed.
  const mainId = providers.data?.find((provider) => provider.is_active)?.id;

  const check = async (provider: AiProvider) => {
    try {
      const out = await test.mutateAsync(provider.id);
      pushToast(
        out.ok
          ? { kind: "success", text: t("aggregator.ai.test_ok", { name: provider.name }) }
          : { kind: "error", text: t("aggregator.ai.test_failed"), detail: out.detail },
      );
    } catch (err) {
      pushToast({ kind: "error", text: errorText(err, t("aggregator.action_error")) });
    }
  };

  return (
    <div className="space-y-5">
      <p className="text-sm text-fg-muted">{t("aggregator.ai.what_for")}</p>
      {providers.data && providers.data.length === 0 && (
        <p className="text-sm text-fg-muted">{t("aggregator.ai.empty")}</p>
      )}
      <ul className="space-y-2">
        {providers.data?.map((provider) => (
          <li
            key={provider.id}
            className="space-y-2 rounded-xl border border-border bg-bg-primary px-4 py-3"
          >
            <div className="flex flex-wrap items-center gap-3">
              <div className="min-w-0 flex-1">
                <div className="flex flex-wrap items-center gap-2">
                  <span className="truncate font-semibold text-fg-primary">{provider.name}</span>
                  <Badge tone={provider.id === mainId ? "accent" : "neutral"}>
                    {t(
                      !provider.is_active
                        ? "aggregator.ai.off"
                        : provider.id === mainId
                          ? "aggregator.ai.main"
                          : "aggregator.ai.spare",
                    )}
                  </Badge>
                </div>
                <div className="truncate font-mono text-xs text-fg-muted">
                  {provider.model} · {t("aggregator.ai.key", { hint: provider.key_hint })}
                </div>
              </div>
              {canEdit && (
                <Button
                  size="sm"
                  variant="outline"
                  loading={test.isPending && test.variables === provider.id}
                  onClick={() => check(provider)}
                >
                  {t("aggregator.ai.test")}
                </Button>
              )}
              {canManage && (
                <>
                  <Switch
                    checked={provider.is_active}
                    aria-label={t("aggregator.ai.active")}
                    onCheckedChange={(is_active) =>
                      update.mutate(
                        { id: provider.id, is_active },
                        {
                          onError: (err) =>
                            pushToast({
                              kind: "error",
                              text: errorText(err, t("aggregator.action_error")),
                            }),
                        },
                      )
                    }
                  />
                  <button
                    type="button"
                    className={ICON_BUTTON}
                    aria-label={t("aggregator.ai.remove")}
                    onClick={() => setRemoving(provider)}
                  >
                    <Trash2 size={16} strokeWidth={2} aria-hidden="true" />
                  </button>
                </>
              )}
            </div>
            {provider.last_error && (
              <p className="text-xs text-danger">
                {t("aggregator.ai.last_error", {
                  when: provider.last_error_at ? formatRelative(provider.last_error_at) : "",
                })}{" "}
                {provider.last_error}
              </p>
            )}
          </li>
        ))}
      </ul>

      {canManage && (
        <div className="space-y-3 rounded-xl border border-border px-4 py-4">
          <h3 className="text-sm font-semibold text-fg-primary">{t("aggregator.ai.add_title")}</h3>
          <div className="grid gap-3 sm:grid-cols-2">
            <div className="space-y-2">
              <Label>{t("aggregator.ai.service")}</Label>
              <SimpleSelect
                value={preset}
                onValueChange={pick}
                options={[
                  ...PROVIDER_PRESETS.map((p) => ({ value: p.id, label: p.name })),
                  { value: "other", label: t("aggregator.ai.other") },
                ]}
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="agg_ai_model">{t("aggregator.ai.model")}</Label>
              <Input
                id="agg_ai_model"
                value={form.model}
                onChange={(e) => setForm({ ...form, model: e.target.value })}
                className="font-mono"
              />
            </div>
          </div>
          {preset === "other" && (
            <div className="grid gap-3 sm:grid-cols-2">
              <div className="space-y-2">
                <Label htmlFor="agg_ai_name">{t("aggregator.ai.name")}</Label>
                <Input
                  id="agg_ai_name"
                  value={form.name}
                  onChange={(e) => setForm({ ...form, name: e.target.value })}
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="agg_ai_url">{t("aggregator.ai.address")}</Label>
                <Input
                  id="agg_ai_url"
                  value={form.base_url}
                  onChange={(e) => setForm({ ...form, base_url: e.target.value })}
                  placeholder="https://…/v1"
                  className="font-mono"
                />
              </div>
            </div>
          )}
          <div className="space-y-2">
            <Label htmlFor="agg_ai_key">{t("aggregator.ai.api_key")}</Label>
            <Input
              id="agg_ai_key"
              type="password"
              autoComplete="off"
              value={form.api_key}
              onChange={(e) => {
                setForm({ ...form, api_key: e.target.value });
                setError(null);
              }}
              className="font-mono"
            />
            <p className="text-xs text-fg-muted">{t("aggregator.ai.api_key_hint")}</p>
          </div>
          {error && <Alert tone="danger">{error}</Alert>}
          <Button
            onClick={submit}
            loading={add.isPending}
            disabled={
              add.isPending ||
              form.api_key.trim().length < 8 ||
              !form.name.trim() ||
              !form.model.trim() ||
              !form.base_url.trim()
            }
            iconLeft={<Plus size={16} strokeWidth={2} aria-hidden="true" />}
          >
            {t("aggregator.ai.add")}
          </Button>
        </div>
      )}

      <ConfirmDialog
        open={removing !== null}
        onOpenChange={(o) => !o && setRemoving(null)}
        tone="danger"
        title={t("aggregator.ai.remove_confirm", { name: removing?.name ?? "" })}
        description={t("aggregator.ai.remove_hint")}
        confirmText={t("aggregator.ai.remove")}
        cancelText={t("common.cancel")}
        onConfirm={async () => {
          if (!removing) return;
          try {
            await remove.mutateAsync(removing.id);
          } catch (err) {
            pushToast({ kind: "error", text: errorText(err, t("aggregator.action_error")) });
          }
        }}
      />
    </div>
  );
}
